import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { relativeTime } from "@/lib/relativeTime";
import { Sparkles, Plus, Check, Eye, EyeOff, Loader2, Clock, ChevronDown } from "lucide-react";

const PAGE_SIZE = 6;

export default function Testimonies() {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ title: "", message: "" });
    const [sent, setSent] = useState(false);

    const url = user?.role === "admin" ? "/testimonies/all" : "/testimonies";

    const load = useCallback(async (reset = true) => {
        const offset = reset ? 0 : items.length;
        if (reset) setLoading(true); else setLoadingMore(true);
        try {
            const { data } = await api.get(url, { params: { limit: PAGE_SIZE, offset } });
            const next = data.items || [];
            setItems((prev) => (reset ? next : [...prev, ...next]));
            setTotal(data.total || 0);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [url, items.length]);

    // Reset when role changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(true); }, [user?.role]);

    const submit = async (e) => {
        e.preventDefault();
        if (!user?.id) { window.location.href = "/login"; return; }
        await api.post("/testimonies", form);
        setForm({ title: "", message: "" });
        setSent(true);
        setShowForm(false);
        await load(true);
        setTimeout(() => setSent(false), 2500);
    };

    const setVisibility = async (t, approved) => {
        await api.post(`/testimonies/${t.id}/visibility`, { approved });
        setItems((prev) => prev.map((x) => (x.id === t.id ? { ...x, approved } : x)));
        // When viewing the public page (non-admin shape), hidden items would no longer appear;
        // reflect that in the total so the "Showing X of Y" label stays truthful.
        if (user?.role !== "admin") {
            setTotal((n) => Math.max(0, n + (approved ? 1 : -1)));
        }
    };

    const hasMore = items.length < total;

    return (
        <div className="parchment min-h-[calc(100vh-4rem)]">
            <div className="mx-auto max-w-7xl px-6 md:px-10 py-12">
                <div className="flex items-end justify-between flex-wrap gap-4">
                    <div>
                        <div className="text-xs tracking-[0.28em] uppercase flex items-center gap-2" style={{ color: "#7F1D1D" }}>
                            <Sparkles size={14} /> Angels on Earth
                        </div>
                        <h1 className="font-display text-4xl sm:text-5xl mt-2" style={{ color: "#1E1B4B" }}>Praise & Worship.</h1>
                        <p className="mt-3 max-w-2xl" style={{ color: "#525252" }}>
                            A continued thanksgiving for all the goodness and favours received. Share your witness so that God is praised.
                        </p>
                        {total > 0 && (
                            <div className="mt-2 text-xs" style={{ color: "#8a8a8a" }} data-testid="testimonies-total">
                                Showing {items.length} of {total} thanksgivings
                            </div>
                        )}
                    </div>
                    <button onClick={() => setShowForm(!showForm)} className="btn-gold inline-flex items-center gap-2" data-testid="testimonies-new-button">
                        <Plus size={16} /> Share a testimony
                    </button>
                </div>

                {showForm && user?.id && (
                    <form onSubmit={submit} className="mt-6 p-card p-6 grid gap-4" data-testid="testimonies-new-form">
                        <input required placeholder="Title — e.g. My son passed the exam"
                               value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                               className="px-4 py-3 rounded-full border" style={{ borderColor: "#E5E0D8", background: "#FDFBF7" }}
                               data-testid="testimonies-title-input" />
                        <textarea required rows={4} placeholder="Tell the story of how God answered…"
                                  value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                                  className="px-4 py-3 rounded-2xl border" style={{ borderColor: "#E5E0D8", background: "#FDFBF7" }}
                                  data-testid="testimonies-message-input" />
                        <div className="flex items-center justify-between">
                            <div className="text-xs" style={{ color: "#525252" }}>
                                {user?.role === "admin" ? "Will be published immediately." : "Awaiting moderator approval before publishing."}
                            </div>
                            <button type="submit" className="btn-primary text-sm" data-testid="testimonies-submit-button">Offer thanksgiving</button>
                        </div>
                    </form>
                )}

                {sent && (
                    <div className="mt-4 p-card p-3 text-sm flex items-center gap-2" style={{ color: "#7F1D1D" }} data-testid="testimonies-sent">
                        <Check size={14} /> Thank you. Your testimony has been received.
                    </div>
                )}

                <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {loading && items.length === 0 && (
                        <div className="col-span-full flex items-center gap-2 text-sm" style={{ color: "#525252" }}>
                            <Loader2 className="animate-spin" size={14} /> Gathering testimonies…
                        </div>
                    )}
                    {!loading && items.length === 0 && (
                        <div className="col-span-full p-card p-10 text-center">
                            <div className="font-display text-2xl" style={{ color: "#1E1B4B" }}>The feed begins with your praise.</div>
                            <div className="text-sm mt-1" style={{ color: "#525252" }}>No testimonies yet — be the first to thank the Lord.</div>
                        </div>
                    )}
                    {items.map((t, i) => (
                        <div key={t.id}
                             className={`p-card p-6 fade-up delay-${(i % 4) + 1}`}
                             style={t.approved === false ? { opacity: 0.55, borderStyle: "dashed" } : {}}
                             data-testid={`testimonies-card-${t.id}`}>
                            <div className="flex items-center justify-between">
                                <div className="text-[10px] tracking-[0.2em] uppercase" style={{ color: "#7F1D1D" }}>
                                    {t.approved === false ? "Hidden" : "Thanksgiving"}
                                </div>
                                {user?.role === "admin" && (
                                    t.approved ? (
                                        <button onClick={() => setVisibility(t, false)}
                                                className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded-full border"
                                                style={{ borderColor: "#E5E0D8", color: "#7F1D1D" }}
                                                data-testid={`testimonies-hide-${t.id}`}>
                                            <EyeOff size={11} /> Hide
                                        </button>
                                    ) : (
                                        <button onClick={() => setVisibility(t, true)}
                                                className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded-full"
                                                style={{ background: "#D4AF37", color: "#1E1B4B" }}
                                                data-testid={`testimonies-show-${t.id}`}>
                                            <Eye size={11} /> Publish
                                        </button>
                                    )
                                )}
                            </div>
                            <div className="font-display text-2xl mt-2" style={{ color: "#1E1B4B" }}>{t.title}</div>
                            <div className="text-sm mt-2" style={{ color: "#525252" }}>{t.message}</div>
                            <div className="mt-4 flex items-center gap-2 text-xs" style={{ color: "#525252" }}>
                                <span>— {t.author_name}</span>
                                {t.created_at && (
                                    <>
                                        <span>·</span>
                                        <span className="inline-flex items-center gap-1" style={{ color: "#8a8a8a" }}>
                                            <Clock size={11} /> {relativeTime(t.created_at)}
                                        </span>
                                    </>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                {hasMore && (
                    <div className="mt-10 flex justify-center">
                        <button
                            onClick={() => load(false)}
                            disabled={loadingMore}
                            className="btn-primary inline-flex items-center gap-2 disabled:opacity-60"
                            data-testid="testimonies-see-more"
                        >
                            {loadingMore ? <Loader2 className="animate-spin" size={14} /> : <ChevronDown size={14} />}
                            {loadingMore ? "Loading…" : `See more thanksgivings (${total - items.length} more)`}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
